import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import { getProviderGuide } from "../../i18n/docs";
import { img } from "../../constants/assets";

const H1_SECTION_IDS = [
  "provider-portal-documentation",
  "esim-status-mapping",
  "request-status-mapping",
  "provider-workflow",
  "1-request-esims",
  "2-view-my-sim-requests",
  "3-esim-inventory-management",
  "4-assign-esim-to-a-customer",
  "5-customer-activation",
  "6-sync-activation-status",
  "customer-management",
  "complete-api-reference",
  "end-to-end-lifecycle",
];

const generalNavItems = [
  { id: "overview", labelKey: "dashboard.documentation.nav.overview", icon: "terms1.svg" },
  { id: "esim-status-mapping", labelKey: "dashboard.documentation.nav.esimStatus", icon: "terms6.svg" },
  { id: "request-status-mapping", labelKey: "dashboard.documentation.nav.requestStatus", icon: "terms2.svg" },
  { id: "provider-workflow", labelKey: "dashboard.documentation.nav.workflow", icon: "terms4.svg" },
];

const screenNavItems = [
  { id: "1-request-esims", labelKey: "dashboard.documentation.nav.requestEsims", icon: "terms6.svg" },
  { id: "2-view-my-sim-requests", labelKey: "dashboard.documentation.nav.mySimRequests", icon: "terms2.svg" },
  { id: "3-esim-inventory-management", labelKey: "dashboard.documentation.nav.esimInventory", icon: "terms7.svg" },
  { id: "4-assign-esim-to-a-customer", labelKey: "dashboard.documentation.nav.assignEsim", icon: "Container (16).svg" },
  { id: "5-customer-activation", labelKey: "dashboard.documentation.nav.customerActivation", icon: "terms8.svg" },
  { id: "6-sync-activation-status", labelKey: "dashboard.documentation.nav.syncStatus", icon: "terms9.svg" },
];

const referenceNavItems = [
  { id: "customer-management", labelKey: "dashboard.documentation.nav.customers", icon: "teerms10.svg" },
  { id: "complete-api-reference", labelKey: "dashboard.documentation.nav.apiReference", icon: "terms5.svg" },
  { id: "end-to-end-lifecycle", labelKey: "dashboard.documentation.nav.lifecycle", icon: "terms7.svg" },
];

// Clears the sticky public navbar when jumping to a section.
const PUBLIC_SCROLL_OFFSET = 100;

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function getTextContent(children) {
  if (Array.isArray(children)) {
    return children.map(getTextContent).join("");
  }
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (children && typeof children === "object" && "props" in children) {
    return getTextContent(children.props.children);
  }
  return "";
}

function MermaidDiagram({ chart }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const renderDiagram = async () => {
      try {
        const { svg } = await mermaid.render(
          `mermaid-${Math.random().toString(36).slice(2)}`,
          chart
        );
        containerRef.current.innerHTML = svg;
      } catch {
        containerRef.current.textContent = chart;
      }
    };

    renderDiagram();
  }, [chart]);

  return <div ref={containerRef} className="api-doc-mermaid" />;
}

function DocNavLink({ item, label, isActive, onClick }) {
  return (
    <button
      type="button"
      className={`legal-nav-link${isActive ? " legal-nav-link--active" : ""}`}
      onClick={() => onClick(item.id)}
      aria-current={isActive ? "true" : undefined}
    >
      <img src={img(item.icon)} alt="" aria-hidden="true" />
      {label}
    </button>
  );
}

function ApiDocumentation({ variant = "dashboard" }) {
  const { t, i18n } = useTranslation();
  // The public (pre-login) page scrolls the window; the dashboard page scrolls
  // its own content pane, which is locked to the viewport height.
  const isPublic = variant === "public";
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(generalNavItems[0].id);
  const contentRef = useRef(null);
  const headingIdState = useRef({ h1: 0, firstH2: false });

  // Reset on every render so heading IDs stay stable across React Strict Mode passes.
  headingIdState.current = { h1: 0, firstH2: false };

  const allNavItems = useMemo(
    () => [
      ...generalNavItems,
      ...screenNavItems,
      ...referenceNavItems,
    ],
    []
  );

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false, theme: "neutral" });
  }, []);

  useEffect(() => {
    setMarkdown(getProviderGuide(i18n.language));
    setActiveSection(generalNavItems[0].id);
    setLoading(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [i18n.language]);

  const scrollToSection = useCallback((id) => {
    const container = contentRef.current;
    if (!container) return;

    const findTarget = () =>
      container.querySelector(`#${CSS.escape(id)}`);

    const scrollToTarget = (element) => {
      if (isPublic) {
        const top =
          window.scrollY + element.getBoundingClientRect().top - PUBLIC_SCROLL_OFFSET;
        window.scrollTo({ top, behavior: "smooth" });
        setActiveSection(id);
        return;
      }

      const offset = 24;
      const top =
        container.scrollTop +
        element.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        offset;

      container.scrollTo({ top, behavior: "smooth" });
      setActiveSection(id);
    };

    const element = findTarget();
    if (element) {
      scrollToTarget(element);
      return;
    }

    // Markdown may not be mounted yet right after a language switch.
    requestAnimationFrame(() => {
      const retryElement = findTarget();
      if (retryElement) scrollToTarget(retryElement);
    });
  }, [isPublic]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container || loading) return;

    const headings = allNavItems
      .map((item) => container.querySelector(`#${CSS.escape(item.id)}`))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          );

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        root: isPublic ? null : container,
        rootMargin: isPublic
          ? `-${PUBLIC_SCROLL_OFFSET}px 0px -70% 0px`
          : "-24px 0px -70% 0px",
        threshold: 0,
      }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [loading, markdown, allNavItems, isPublic]);

  const markdownComponents = useMemo(() => {
    return {
      h1: ({ children }) => {
        const text = getTextContent(children);
        const state = headingIdState.current;
        const id =
          state.h1 < H1_SECTION_IDS.length
            ? H1_SECTION_IDS[state.h1++]
            : slugify(text);
        const className =
          id === "provider-portal-documentation"
            ? "api-doc-main-title"
            : "legal-section-title api-doc-section-heading";
        return (
          <h1 id={id} className={className}>
            {children}
          </h1>
        );
      },
      h2: ({ children }) => {
        const text = getTextContent(children);
        const state = headingIdState.current;
        const id = !state.firstH2
          ? ((state.firstH2 = true), "overview")
          : slugify(text);
        return (
          <h2 id={id} className="legal-section-title api-doc-section-heading">
            {children}
          </h2>
        );
      },
      h3: ({ children }) => (
        <h3 className="api-doc-subtitle">{children}</h3>
      ),
      p: ({ children }) => <p className="legal-text">{children}</p>,
      ul: ({ children }) => <ul className="legal-list api-doc-list">{children}</ul>,
      ol: ({ children }) => <ol className="api-doc-ordered-list">{children}</ol>,
      li: ({ children }) => <li>{children}</li>,
      strong: ({ children }) => <strong>{children}</strong>,
      blockquote: ({ children }) => (
        <blockquote className="api-doc-blockquote">{children}</blockquote>
      ),
      table: ({ children }) => (
        <div className="api-doc-table-wrap">
          <table className="api-doc-table">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead>{children}</thead>,
      tbody: ({ children }) => <tbody>{children}</tbody>,
      tr: ({ children }) => <tr>{children}</tr>,
      th: ({ children }) => <th>{children}</th>,
      td: ({ children }) => <td>{children}</td>,
      code: ({ className, children }) => {
        const content = String(children).replace(/\n$/, "");
        if (className === "language-mermaid") {
          return <MermaidDiagram chart={content} />;
        }
        if (className?.startsWith("language-") || content.includes("\n")) {
          return <pre className="api-doc-pre"><code>{content}</code></pre>;
        }
        return <code className="api-code">{content}</code>;
      },
      pre: ({ children }) => <>{children}</>,
      hr: () => <hr className="api-doc-divider" />,
    };
  }, [markdown]);

  const page = (
    <>
      {!isPublic && (
        <DashboardPageHeader title={t("dashboard.documentation.title")} />
      )}
      <div className={isPublic ? "legal-layout" : "api-doc-layout"}>
        <aside className="legal-sidebar api-doc-sidebar">
          <div className="api-doc-sidebar-header">
            <img src={img("Background (15).svg")} alt="" aria-hidden="true" />
            <div>
              <h1 className="legal-sidebar-title api-doc-sidebar-title">
                {t("dashboard.documentation.apiDocTitle")}
              </h1>
              <p className="api-doc-version">v2.1.0-stable</p>
            </div>
          </div>

          <nav className="legal-nav" aria-label={t("dashboard.documentation.navAriaLabel")}>
            {generalNavItems.map((item) => (
              <DocNavLink
                key={item.id}
                item={item}
                label={t(item.labelKey)}
                isActive={activeSection === item.id}
                onClick={scrollToSection}
              />
            ))}

            <p className="api-doc-endpoints-label">{t("dashboard.documentation.screens")}</p>

            {screenNavItems.map((item) => (
              <DocNavLink
                key={item.id}
                item={item}
                label={t(item.labelKey)}
                isActive={activeSection === item.id}
                onClick={scrollToSection}
              />
            ))}

            <p className="api-doc-endpoints-label">{t("dashboard.documentation.reference")}</p>

            {referenceNavItems.map((item) => (
              <DocNavLink
                key={item.id}
                item={item}
                label={t(item.labelKey)}
                isActive={activeSection === item.id}
                onClick={scrollToSection}
              />
            ))}
          </nav>
        </aside>

        <div
          ref={contentRef}
          className={`legal-content api-doc-content${
            isPublic ? "" : " api-doc-scroll-content"
          }`}
        >
          {loading ? (
            <p className="legal-text">{t("dashboard.documentation.loading")}</p>
          ) : (
            <ReactMarkdown
              key={i18n.language}
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {markdown}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </>
  );

  if (isPublic) {
    return (
      <section className="legal-page api-doc-page api-doc-page--public py-50">
        <div className="container">{page}</div>
      </section>
    );
  }

  return (
    <section className="dashboard-page legal-page api-doc-page">{page}</section>
  );
}

export default ApiDocumentation;
